import type { IndexState } from '../../../lib/content-index/types';

// This catalogue owns both allowed route fields and removal of old filters.
const QUERY_KEYS = [
  'ciReset',
  'view',
  'domain',
  'group',
  'bucket',
  'status',
  'facetMode',
  'sourceRevision',
  'tab',
  'lang',
  'indexState',
  'category',
  'vectorDb',
  'search',
  'sortBy',
  'sortOrder',
] as const;
type QueryKey = (typeof QUERY_KEYS)[number];
export type ContentIndexQuery = Partial<Record<Exclude<QueryKey, 'indexState' | 'sortOrder'>, string>> & {
  indexState?: IndexState;
  sortOrder?: 'asc' | 'desc';
};
export type UrlNavigationState = Omit<ContentIndexQuery, 'view' | 'domain' | 'ciReset'>;

export function normalizeUrlNavigation(value?: UrlNavigationState | null): UrlNavigationState {
  return {
    group: value?.group || undefined,
    bucket: value?.bucket || undefined,
    status: value?.status || undefined,
    facetMode: value?.status === 'c200' && value?.facetMode === 'summary' ? 'summary' : undefined,
    sourceRevision: value?.status === 'c200' && value?.facetMode === 'summary' && /^[0-9a-f]{64}$/.test(value?.sourceRevision || '') ? value?.sourceRevision : undefined,
    tab: value?.tab === 'crawl' ? undefined : value?.tab || undefined,
    lang: value?.lang || undefined,
    indexState: value?.status || value?.bucket || value?.vectorDb ? undefined : value?.indexState,
    category: value?.category || undefined,
    vectorDb: value?.vectorDb || undefined,
    search: value?.search || undefined,
    sortBy: value?.sortBy && !['last', 'lastCrawled', 'last_crawled'].includes(value.sortBy) ? value.sortBy : undefined,
    sortOrder: value?.sortOrder === 'asc' ? 'asc' : undefined,
  };
}

export function readContentIndexQuery(href: string): ContentIndexQuery {
  const p = new URL(href).searchParams;
  return {
    ciReset: p.get('ciReset') || undefined,
    view: p.get('view') || undefined,
    domain: p.get('domain') || undefined,
    group: p.get('group') || undefined,
    bucket: p.get('bucket') || undefined,
    status: p.get('status') || undefined,
    facetMode: p.get('status') === 'c200' && p.get('facetMode') === 'summary' ? 'summary' : undefined,
    sourceRevision: p.get('status') === 'c200' && p.get('facetMode') === 'summary' && /^[0-9a-f]{64}$/.test(p.get('sourceRevision') || '') ? p.get('sourceRevision') || undefined : undefined,
    tab: p.get('tab') || undefined,
    lang: p.get('lang') || undefined,
    indexState:
      p.has('status') || p.has('bucket') || p.has('vectorDb')
        ? undefined
        : p.get('indexState') === 'indexed'
        ? 'indexed'
        : p.get('indexState') === 'notIndexed'
        ? 'notIndexed'
        : undefined,
    category: p.get('category') || undefined,
    vectorDb: p.get('vectorDb') || undefined,
    search: p.get('search') || undefined,
    sortBy: p.get('sortBy') || undefined,
    sortOrder: p.get('sortOrder') === 'asc' ? 'asc' : p.get('sortOrder') === 'desc' ? 'desc' : undefined,
  };
}

export function contentIndexUrl(href: string, next: ContentIndexQuery): string {
  const url = new URL(href);
  for (const key of QUERY_KEYS) url.searchParams.delete(key);
  for (const key of QUERY_KEYS) {
    const value = next[key];
    if (value && !(key === 'view' && value === 'today') && !(key === 'domain' && value === 'all')) {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}
